import React from "react";
import { trpc } from "../../main/components/trpc.ts";
import { inferRouterOutputs } from "@trpc/server/unstable-core-do-not-import";
import type { AppRouter } from "cadendar-trpc-server";

type PecRouterOutput = inferRouterOutputs<AppRouter>["pec"];
type MutuelleRow = PecRouterOutput["getMutuellesData"][number];

interface MutuellesAdminPanelProps {
  networkName: string;
  networkLabel: string;
}

function normalizeAmcNumber(value: string) {
  return value.replace(/\s+/g, "").trim();
}

const MutuellesAdminPanel = ({
  networkName,
  networkLabel,
}: MutuellesAdminPanelProps) => {
  const trpcUtils = trpc.useContext();
  const [filter, setFilter] = React.useState("");
  const [draftById, setDraftById] = React.useState<Record<string, string>>({});
  const [lastSavedId, setLastSavedId] = React.useState<string | null>(null);

  const {
    data: mutuelles,
    isLoading,
    error,
  } = trpc.pec.getMutuellesData.useQuery(networkName);

  const updateMutuelleAmcNumberMutation =
    trpc.pec.updateMutuelleAmcNumber.useMutation({
      onSuccess: async (_data, variables) => {
        setLastSavedId(variables.mutuelleId);
        await trpcUtils.pec.getMutuellesData.invalidate(networkName);
      },
    });

  const filteredMutuelles = mutuelles?.filter((mutuelle: MutuelleRow) => {
      const normalizedFilter = filter.trim().toLowerCase();

      if (!normalizedFilter) {
        return true;
      }

      return (
        mutuelle.name.toLowerCase().includes(normalizedFilter) ||
        (mutuelle.amc_number || "").toLowerCase().includes(normalizedFilter) ||
        (mutuelle.alias || []).some((alias) =>
          alias.toLowerCase().includes(normalizedFilter),
        )
      );
    });

  const getDraftValue = (mutuelle: MutuelleRow) => {
    return draftById[mutuelle.id] ?? mutuelle.amc_number ?? "";
  };

  const saveAmcNumber = (mutuelle: MutuelleRow) => {
    updateMutuelleAmcNumberMutation.mutate({
      mutuelleId: mutuelle.id,
      amcNumber: normalizeAmcNumber(getDraftValue(mutuelle)) || null,
    });
  };

  return (
    <div>
      <div style={{ marginBottom: "12px" }}>
        <h2>{networkLabel}</h2>
        <p style={{ color: "#666", fontSize: "0.9rem" }}>
          Renseigner ici le vrai numéro AMC quand il est connu. Le matching OCR
          l'utilisera avant le nom.
        </p>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <input
          type="text"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filtrer par nom, alias ou AMC"
          style={{ width: "100%", maxWidth: "420px", padding: "8px" }}
        />
      </div>

      {isLoading ? <p style={{ color: "#666" }}>Chargement des mutuelles...</p> : null}
      {error ? (
        <p style={{ color: "#b42318" }}>
          {error.message || "Impossible de charger les mutuelles."}
        </p>
      ) : null}
      {updateMutuelleAmcNumberMutation.error ? (
        <p style={{ color: "#b42318" }}>
          {updateMutuelleAmcNumberMutation.error.message ||
            "Impossible d'enregistrer le numéro AMC."}
        </p>
      ) : null}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Mutuelle</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Alias</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>AMC</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredMutuelles?.map((mutuelle) => {
              const isPending =
                updateMutuelleAmcNumberMutation.isPending &&
                updateMutuelleAmcNumberMutation.variables?.mutuelleId === mutuelle.id;
              const isSaved = lastSavedId === mutuelle.id && !isPending;

              return (
                <tr key={mutuelle.id}>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    <div>{mutuelle.name}</div>
                    {mutuelle.input_hint ? (
                      <div style={{ color: "#666", fontSize: "0.8rem" }}>
                        {mutuelle.input_hint}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {(mutuelle.alias || []).join(", ") || "-"}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    <input
                      type="text"
                      value={getDraftValue(mutuelle)}
                      onChange={(event) => {
                        setDraftById((previousValue) => ({
                          ...previousValue,
                          [mutuelle.id]: event.target.value,
                        }));
                        setLastSavedId(null);
                      }}
                      placeholder="Numéro AMC"
                      style={{ width: "180px", padding: "6px" }}
                    />
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    <button
                      type="button"
                      onClick={() => saveAmcNumber(mutuelle)}
                      disabled={isPending}
                    >
                      {isPending ? "Enregistrement..." : "Enregistrer"}
                    </button>
                    {isSaved ? (
                      <div style={{ color: "#0f766e", fontSize: "0.8rem" }}>
                        Enregistré
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {!isLoading && filteredMutuelles?.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{ border: "1px solid #ddd", padding: "8px", color: "#666" }}
                >
                  Aucune mutuelle ne correspond au filtre.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MutuellesAdminPanel;
